import { registerFigure } from "./base.js";
/**
 * Labelled bars, drawn in proportion to real numbers.
 *
 * The class of question no arrangement of words can answer, because the answer
 * IS the number: how big the Sun is next to the Earth, how long animals live,
 * which planet is biggest, how deep the ocean goes. "A hundred and nine times
 * wider" is a fact a child reads and does not feel; a bar a hundred and nine
 * times longer is one they do.
 *
 * This is the one kind that needed a new wire field (`values`), which is why it
 * was worth being sure about — see the note in base.ts.
 *
 * The renderer handles ratios too large to draw honestly; validation's job is
 * only to insist the numbers are real and line up with the labels.
 */
export const scaleFigure = registerFigure({
    kind: "scale",
    summary: "Labelled bars, in proportion to real quantities.",
    chooseWhen: "The point of the answer is how much bigger, longer, heavier or older one " +
        "thing is than another — Sun against Earth, how long different animals " +
        "live, the sizes of the planets. Only use it when you are confident of the " +
        "actual numbers, and put the unit in the caption.",
    issues(figure) {
        const labels = figure.labels ?? [];
        const values = figure.values ?? [];
        if (labels.length < 2)
            return ["A scale needs at least two things to compare."];
        if (labels.length > 5)
            return ["More than 5 bars stops being readable."];
        if (values.length !== labels.length) {
            return [`${values.length} numbers for ${labels.length} labels — there must be one each.`];
        }
        // A negative or zero bar has no length, and a comparison containing one is
        // not a comparison a bar chart can make.
        if (values.some((v) => v <= 0))
            return ["Every value must be greater than zero."];
        if (labels.some((l) => l.trim().length === 0))
            return ["A label cannot be blank."];
        return [];
    },
    normalise(figure) {
        const labels = (figure.labels ?? []).map((l) => l.trim());
        return {
            kind: "scale",
            caption: figure.caption,
            labels,
            values: (figure.values ?? []).slice(0, labels.length),
        };
    },
    example: {
        kind: "scale",
        caption: "Widths, in thousands of kilometres.",
        labels: ["Earth", "Neptune", "Jupiter"],
        values: [12.7, 49.2, 139.8],
    },
});
//# sourceMappingURL=scale.js.map